import React from 'react';
import { subDays } from 'date-fns';
import { Divider } from '@components/Feed/Divider';
import { ActivityFeedGroupFilterOption } from '@graphql/reactQuery';
import { ArtistDropSkeleton } from './ArtistDrop/Skeleton';
import { PlaylistCreatedSkeleton } from './PlaylistCreated/Skeleton';
import { UserCollectedManySkeleton } from './UserCollectedManySongs/Skeleton';
import { PaddingContainer } from './shared';
import type { ActivityGroup } from './store';
import { PAGE_SIZE } from './store';

/**
 * Only shows when hasNextPage is false
 * hasNextPage returns undefined when loading or refetching
 * We check explicitly for false, because we want to show the skeleton even when refetching
 * Date shows the date before the last item's date
 * If no date is provided, it will show the current date
 */
const Skeleton = ({
  lastFeedItemTimestamp,
  hasNextPage,
  activityFeedGroupFilter,
}: {
  lastFeedItemTimestamp?: number;
  hasNextPage: boolean;
  activityFeedGroupFilter: ActivityFeedGroupFilterOption[];
}): JSX.Element | null => {
  const date = lastFeedItemTimestamp
    ? subDays(new Date(lastFeedItemTimestamp || 0), 1)
    : new Date();

  const { Collected, ReleaseDropped, AddedToPlaylist, Likes } = ActivityFeedGroupFilterOption;

  const activityFeedGroupFilterWithoutAll = activityFeedGroupFilter?.filter(
    type => type !== ActivityFeedGroupFilterOption.All,
  );

  const pickFilteredComponentsIfAny = (): ActivityFeedGroupFilterOption[] =>
    activityFeedGroupFilterWithoutAll?.length
      ? activityFeedGroupFilterWithoutAll
      : Object.values(ActivityFeedGroupFilterOption);

  return hasNextPage !== false ? (
    <PaddingContainer>
      <Divider date={date} />
      {([] as ActivityFeedGroupFilterOption[])
        .concat(...Array.from({ length: 10 }, pickFilteredComponentsIfAny))
        .slice(0, PAGE_SIZE)
        .map((component, i) => (
          <div key={i}>
            {component === Collected && <UserCollectedManySkeleton />}
            {component === ReleaseDropped && <ArtistDropSkeleton />}
            {component === AddedToPlaylist && <PlaylistCreatedSkeleton />}
            {component === Likes && <PlaylistCreatedSkeleton />}
            <Divider />
          </div>
        ))}
    </PaddingContainer>
  ) : null;
};

export const Footer = ({
  data,
  hasNextPage,
  activityFeedGroupFilter,
}: {
  data: ActivityGroup[];
  hasNextPage: boolean;
  activityFeedGroupFilter: ActivityFeedGroupFilterOption[];
}) => {
  return (
    <Skeleton
      lastFeedItemTimestamp={data[data.length - 1]?.latestActivityOccurenceAt}
      hasNextPage={hasNextPage}
      activityFeedGroupFilter={activityFeedGroupFilter}
    />
  );
};
